// --------------------
// COMMON TYPE ALIASES
// --------------------
export type Timestamp = number;
export type ID = string;
export type Url = string;

// --------------------
// BASE INTERFACES
// --------------------
export interface InitialTrackingEvent {
    eventName: string;
    timestamp: Timestamp;
    solvId: ID | null;
    sessionId: ID;
    pageUrl: Url;
    referrerUrl: Url;
    eventSource: string;
    eventLabel: string;
    deviceType: string;
    operatingSystem: string;
    browser: string;
    browserVersion: string;
}

export interface BaseEvent {
    timestamp?: Timestamp;
}

export interface BaseOrderEvent extends BaseEvent {
    orderNumber: ID;
}

export interface BaseMultiOrderEvent extends BaseEvent {
    orderNumbers: ID[];
}

export interface BaseUserEvent {
    sellerId: ID;
    buyerId: ID;
}

export interface BaseAddressEvent {
    pickupAddress: string;
    deliveryAddress: string;
}

export interface BaseOrderStatusEvent extends BaseOrderEvent {
    orderStatus: string;
    orderValue: number;
    orderBucketOrTab: string;
}

// --------------------
// EVENT INTERFACES
// --------------------
export interface CancelOrderTrackingEvent
    extends BaseOrderEvent,
        BaseUserEvent,
        BaseAddressEvent {
    reason: string;
}

export interface ViewOrderDetailsEvent
    extends BaseOrderStatusEvent,
        BaseUserEvent,
        BaseAddressEvent {
    primaryReturnReason: string;
    secondaryReturnReason: string;
    remarks: string;
    deliveredOn: string;
    returnInitiatedOn: string;
}

export interface SearchOrderTrackingEvent extends BaseEvent {
    dropdownSelection: string;
    searchValue: string;
    warehouse: string;
    numberOfResults: number;
}

export interface GenerateInvoiceTrackingEvent
    extends Omit<BaseOrderStatusEvent, 'orderNumber'>,
        BaseUserEvent,
        BaseAddressEvent {
    orderNumber: ID | ID[];
    paymentMode: string;
    invoiceGeneratedOn: string;
    invoiceNumber: string;
    invoiceValue: number;
    invoiceGeneratedFrom: string;
}

export interface DownloadDocumentTrackingEvent extends BaseEvent {
    documentType: string;
    orderIds: ID[];
    numberOfOrders: number;
}

export interface DownloadServiceInvoiceTrackingEvent extends BaseEvent {
    selectedMonth: string;
    selectedYear: string;
    documentType: string;
}

export interface DownloadRecentsDocumentTrackingEvent extends BaseEvent {
    fileName: string;
}

export interface TrackOrderTrackingEvent extends BaseOrderEvent {
    orderDate: string;
    pickedOn: string;
    carrier: string;
    estimatedDeliveryDate: string;
    status: string;
}

export interface GenerateManifestTrackingEvent extends BaseMultiOrderEvent {
    awbNumbers: string[];
    numberOfOrders: number;
    numberOfParcels: number;
    numberOfAwbs: number;
    orderBucketOrTab: string;
    carrier: string;
    manifestId: string;
}

export interface FilterTrackingEvent {
    [filterName: string]:
        | string
        | number
        | boolean
        | Array<string | number | boolean>;
    numberOfResults: number;
}

export interface BookShipmentTrackingEvent
    extends BaseMultiOrderEvent,
        BaseUserEvent,
        BaseAddressEvent {
    orderStatus: string;
    orderValue: number;
    orderBucketOrTab: string;
    awbNumber: string;
    carrier: string;
    numberOfParcels: number;
    showDetailsClicked: boolean;
    totalWeightEdited: boolean;
    previousTotalWeight: number;
    newTotalWeight: number;
    uomChanged: boolean;
    uomPreviousValue: string;
    uomNewValue: string;
    skuEditLinkClicked: boolean;
    skuId: string;
    weightEdited: boolean;
    previousWeight: number;
    newWeight: number;
    shippingLabelGenerated: boolean;
}

export interface MoveToReadyToShipTrackingEvent extends BaseMultiOrderEvent {
    manifestId: string;
    movedOn: string;
    movedFrom: string;
}

export interface SessionStartedTrackingEvent extends BaseEvent {
    orgId: string;
    analyticsId: string;
    designation: string;
    freshChatRestoreId: string;
}

export interface SessionResumedTrackingEvent extends BaseEvent {
    companyNumber: string;
}

export interface ApiCallTrackingEvent extends BaseEvent {
    url: Url;
    method: string;
    headers?: Record<string, string>;
    body?: any;
    statusCode?: number;
    statusText?: string;
    responseBody?: any;
}

export interface PageNavigationTrackingEvent extends BaseEvent {
    fromUrl?: Url;
    toUrl: Url;
    durationMs?: number;
}

export interface PageClickTrackingEvent extends BaseEvent {
    linkLabel: string;
    route: string;
    clickedAt: Timestamp;
}

export interface TabChangeTrackingEvent extends BaseEvent {
    fromTab: string;
    toTab: string;
    changedAt: Timestamp;
    durationMs: number;
}

export interface BatchProcessDownloadTrackingEvent extends BaseEvent {
    selectedDate: string;
    fileName: string;
}

export interface BatchProcessUploadTrackingEvent extends BaseEvent {
    fileName: string;
    selectedAddress: string;
}

export interface ViewJobDetailsTrackingEvent extends BaseEvent {
    jobId: string;
    jobDesc: string;
    createdOn: string;
    jobStatus: string;
}

export interface DownloadResultSheetTrackingEvent extends BaseEvent {
    fileName: string;
    downloadedOn: Timestamp;
}

export interface DownloadOriginalSheetTrackingEvent extends BaseEvent {
    fileName: string;
    downloadedOn: Timestamp;
}

export interface MenuExpandedTrackingEvent extends BaseEvent {
    menuLabel: string;
    expanded: boolean;
    expandedAt: Timestamp;
}

export interface PageViewTrackingEvent extends BaseEvent {
    pageName: string;
    viewedAt: Timestamp;
}

export interface RequestDeleteAccountTrackingEvent extends BaseEvent {
    reason?: string;
    userType: string; // 'owner', 'employee', etc.
}

export interface UploadBusinessImageTrackingEvent extends BaseEvent {
    fileName: string;
    imageType: string; // 'primary', 'secondary'
}

export interface RemoveBusinessImageTrackingEvent extends BaseEvent {
    imageType: string; // 'primary', 'secondary'
    removalMethod: string; // 'delete_button', 'replace'
}

export interface SaveBusinessAddressTrackingEvent extends BaseEvent {
    addressType: string;
    city: string;
    state: string;
    addressLine1: string;
    addressLine2: string;
    adrDefaultStatus: boolean;
    contactName: string;
    contactNumber: string;
    fullAddress: string;
    landmark: string;
    latitude: number;
    longitude: number;
    nickName: string;
    contactEmail: string;
    pin: string;
    platformType: string;
    status: string;
}

export interface SaveBusinessDetailsTrackingEvent extends BaseEvent {
    companyName: string;
    employeeCount: number;
    imageCount: number;
    addressCount: number;
    hasAboutCompany: boolean;
    defaultAddressType: string;
    defaultAddressCity: string;
    defaultAddressState: string;
}

export interface AddNewAddressTrackingEvent extends BaseEvent {
    addressType: string;
    currentAddressCount: number;
}

export interface DeleteTeamMemberTrackingEvent extends BaseEvent {
    memberName: string;
    memberRole: string;
    deletedByRole: string; // role of person performing deletion
}

export interface LogoutTrackingEvent extends BaseEvent {
    sessionDuration?: number; // in milliseconds
    logoutMethod: string; // 'manual', 'timeout', 'forced'
    lastPageVisited?: string;
}

export interface CancelBusinessDetailsTrackingEvent extends BaseEvent {
    hasUnsavedChanges: boolean;
}

export interface CancelAddNewAddressTrackingEvent extends BaseEvent {
    addressType: string;
}

export interface CancelDeleteMemberTrackingEvent extends BaseEvent {
    memberRole: string;
    memberName: string;
}

export interface DownloadReportTrackingEvent extends BaseEvent {
    type: string;
    requestedOn: string;
    preparedOn: string;
    downloadMethod: string; // 'direct_download', 'generated_report'
    fileFormat: string; // 'xlsx', 'pdf', 'zip', 'json'
}

export interface RequestReportTrackingEvent extends BaseEvent {
    type: string;
    dateRange: string;
    filtersApplied: string[];
}

// --------------------
// PAYLOAD WRAPPER
// --------------------
export class TrackingEventPayLoad<T = any> {
    readonly event = 'solv_seller_portal_events';
    readonly portal = 'solvsellerportal';
    readonly eid: string;
    readonly properties: TrackingEvent<T>;

    constructor(init: { eid: string; properties: TrackingEvent<T> }) {
        this.eid = init.eid;
        this.properties = init.properties;
    }
}

// --------------------
// GENERIC TYPES
// --------------------
export type TrackingInput<T> = Partial<InitialTrackingEvent> &
    T & { eid?: string };
export type TrackingEvent<T> = InitialTrackingEvent & T;

// --------------------
// UNION OF ALL EVENTS
// --------------------
export type AppTrackingEvents =
    | CancelOrderTrackingEvent
    | ViewOrderDetailsEvent
    | SearchOrderTrackingEvent
    | GenerateInvoiceTrackingEvent
    | DownloadDocumentTrackingEvent
    | DownloadServiceInvoiceTrackingEvent
    | DownloadRecentsDocumentTrackingEvent
    | TrackOrderTrackingEvent
    | GenerateManifestTrackingEvent
    | FilterTrackingEvent
    | BookShipmentTrackingEvent
    | MoveToReadyToShipTrackingEvent
    | SessionStartedTrackingEvent
    | SessionResumedTrackingEvent
    | ApiCallTrackingEvent
    | PageNavigationTrackingEvent
    | PageClickTrackingEvent
    | TabChangeTrackingEvent
    | BatchProcessDownloadTrackingEvent
    | BatchProcessUploadTrackingEvent
    | ViewJobDetailsTrackingEvent
    | DownloadResultSheetTrackingEvent
    | DownloadOriginalSheetTrackingEvent
    | MenuExpandedTrackingEvent
    | PageViewTrackingEvent
    | RequestDeleteAccountTrackingEvent
    | UploadBusinessImageTrackingEvent
    | RemoveBusinessImageTrackingEvent
    | SaveBusinessAddressTrackingEvent
    | SaveBusinessDetailsTrackingEvent
    | AddNewAddressTrackingEvent
    | DeleteTeamMemberTrackingEvent
    | LogoutTrackingEvent
    | CancelBusinessDetailsTrackingEvent
    | CancelAddNewAddressTrackingEvent
    | CancelDeleteMemberTrackingEvent
    | DownloadReportTrackingEvent
    | RequestReportTrackingEvent;
